import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, mergeMap, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { createEffectFactory } from '@core/ngrx-legacy';

import { JobRequisitionsHttpService } from './job-requisitions-http.service';
import { JobRequisitionsActions } from './job-requisitions.store';
import { JobsWrapperActionsService } from '../jobs/jobs-wrapper/jobs-wrapper-actions.service';

@Injectable({
  providedIn: 'root',
})
export class JobRequisitionsEffects {
  private readonly actions$ = inject(Actions);
  private readonly jobRequisitionsHttpService = inject(JobRequisitionsHttpService);
  private readonly jobsWrapperActionsService = inject(JobsWrapperActionsService);

  public effects = createEffectFactory(JobRequisitionsActions, this.actions$);

  public fetchOne$ = this.effects(JobRequisitionsActions.fetchOne, ({ payload }) =>
    this.jobRequisitionsHttpService.fetchOne$(payload.offerId).pipe(
      map(response => ({
        data: {
          requisitions: response.requisitions,
          offerId: payload.offerId,
        },
      })),
    ),
  );

  public assign$ = this.effects(JobRequisitionsActions.assign, ({ payload }) =>
    this.jobRequisitionsHttpService.assign$(payload).pipe(
      switchMap(response => [
        JobRequisitionsActions.assignSuccess({
          payload: {
            offerId: payload.offerId,
            data: response.requisition,
            copyData: payload.copyData,
          },
        }),
      ]),
      catchError(error => [JobRequisitionsActions.assignFailed({ payload: { error } })]),
    ),
  );

  public assignOpenings$ = this.effects(
    JobRequisitionsActions.assignOpenings,
    ({ payload }) => {
      const filledOpeningsCount = Object.values(payload.assignmentsByRequisitionId).reduce(
        (acc, assignments) => acc + assignments.length,
        0,
      );

      return forkJoin(
        Object.entries(payload.assignmentsByRequisitionId).map(([requisitionId, assignments]) =>
          this.jobRequisitionsHttpService.assignOpenings$({
            offerId: payload.offerId,
            requisitionId: parseInt(requisitionId, 10),
            fillOpenings: assignments,
          }),
        ),
      ).pipe(
        switchMap(() => [
          JobRequisitionsActions.assignOpeningsSuccess({
            payload: {
              offerId: payload.offerId,
              filledOpeningsCount,
            },
          }),
        ]),
        catchError(error => [JobRequisitionsActions.assignOpeningsFailed({ payload: { error } })]),
      );
    },
    { customMapOperator: mergeMap },
  );

  public unassign$ = this.effects(JobRequisitionsActions.unassign, ({ payload }) =>
    this.jobRequisitionsHttpService.unassign$(payload).pipe(
      switchMap(() => [
        JobRequisitionsActions.unassignSuccess({
          payload: {
            offerId: payload.offerId,
            requisitionId: payload.requisitionId,
          },
        }),
      ]),
      catchError(error => [JobRequisitionsActions.unassignFailed({ payload: { error } })]),
    ),
  );

  public fetchDiff$ = this.effects(JobRequisitionsActions.fetchDiff, ({ payload }) =>
    this.jobRequisitionsHttpService.fetchDiff$(payload).pipe(
      switchMap(response => [JobRequisitionsActions.fetchDiffSuccess({ payload: response })]),
      catchError(error => [JobRequisitionsActions.fetchDiffFailed({ payload: { error } })]),
    ),
  );

  public copyData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobRequisitionsActions.copyData),
      switchMap(({ payload }) =>
        this.jobRequisitionsHttpService
          .copyData$(payload.offerId, payload.requisitionId)
          .pipe(map(response => response.offer)),
      ),
      switchMap(offer => [
        JobRequisitionsActions.copyDataSuccess({ payload: { data: { offer } } }),
        this.jobsWrapperActionsService.jobsFullUpdateSuccessAction({
          payload: { id: offer.id, data: offer },
        }),
      ]),
      catchError(error => of(JobRequisitionsActions.copyDataFailed({ payload: error }))),
    ),
  );
}
